import {
    Button,
    Divider,
    FormControlLabel,
    Grid,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import {
    CF_CONTRACT_SYSTEMS,
    CONTRACT_STATUS,
    INPUT_VALIDATION,
    PAGINATION,
} from "../../../constants";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
    IMAGE_SIZE_ERROR_MESSAGE,
    RichTextEditor,
    isImageSizeError,
} from "../../../components";
import { getMonth, getYear, parseISO } from "date-fns";
import {
    useFetch,
    useIsBasicRole,
    useSnackbar,
    useUsers,
} from "../../../hooks";
import useSWR, { mutate as globalMutate } from "swr";

import { BACKEND_ROUTES } from "../../../backendRoutes";
import { ContractFormSkeleton } from "./ContractFormSkeleton";
import { DataAllowanceForm } from "./DataAllowanceForm";
import { DatePicker } from "@mui/x-date-pickers";
import { DatePickerField } from "./DatePickerField";
import { FRONTEND_ROUTES } from "../../../frontendRoutes";
import { FetchErrorAlert } from "../../../components/FetchErrorAlert";
import { LoadingButton } from "@mui/lab";
import { formatDateISO } from "../../../utils/formatTime";
import { useNavigate } from "react-router-dom";
import useSWRImmutable from "swr/immutable";
import { useState } from "react";

export const ContractForm = ({ contract, mutate }) => {
    const navigate = useNavigate();
    const { post, patch } = useFetch();
    const isBasicRole = useIsBasicRole();
    const { openSnackbar } = useSnackbar();
    const [reportDateError, setDateError] = useState();
    const {
        data: users,
        error: usersError,
        isLoading: areUsersLoading,
    } = useUsers();
    const {
        data: companies,
        error: companiesError,
        isLoading: areCompaniesLoading,
    } = useSWR(
        contract
            ? null
            : `${BACKEND_ROUTES.COMPANY}?sort=name&limit=${PAGINATION.COMPANY.LIMIT.MAX}`
    );
    const {
        data: uploadTemplates,
        error: uploadTemplatesError,
        isLoading: areUploadTemplatesLoading,
    } = useSWRImmutable(BACKEND_ROUTES.UPLOAD_TEMPLATES);

    const {
        data: sites,
        error: sitesError,
        isLoading: areSitesLoading,
    } = useSWR(
        contract
            ? `${BACKEND_ROUTES.SITE}?contractUuid=${contract.uuid}&limit=${PAGINATION.GENERIC.LIMIT.MAX}`
            : null
    );

    const methods = useForm({
        disabled: isBasicRole,
        defaultValues: {
            name: "",
            companyUuid: "",
            startDate: null,
            endDate: null,
            processingLeadUuid: "",
            campaignManagerUuid: "",
            salespersonUuid: "",
            reportDate: null,
            status: CONTRACT_STATUS.PRE_ORDERED,
            goal: "",
            processingComments: null,
            system: "",
            uploadTemplateId: "",
            hasExperiment: false,
            applyProcessingLeadToAllSites: false,
            dataAllowances: [],
        },
        values: contract && {
            name: contract.name,
            companyUuid: contract.companyUuid,
            startDate: parseISO(contract.startDate),
            endDate: parseISO(contract.endDate),
            processingLeadUuid: contract.processingLeadUuid,
            campaignManagerUuid: contract.campaignManagerUuid,
            salespersonUuid: contract.salespersonUuid,
            reportDate: contract.reportDate
                ? parseISO(contract.reportDate)
                : null,
            status: contract.status,
            goal: contract.goal ?? "",
            system: contract.system ?? "",
            uploadTemplateId: contract.UploadTemplate?.id ?? "",
            hasExperiment: contract.hasExperiment ?? false,
            applyProcessingLeadToAllSites: false,
            dataAllowances: contract.DataAllowances.map((dataAllowance) => ({
                system: dataAllowance.system,
                siteCount: dataAllowance.siteCount,
                featureCount: dataAllowance.featureCount,
                acquisitionCount: dataAllowance.acquisitionCount,
                monthlyAcquisitions: dataAllowance.MonthlyAcquisitions.map(
                    ({ date, count }) => ({
                        count,
                        date,
                    })
                ),
            })),
        },
    });
    const {
        control,
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting, isDirty },
    } = methods;

    const mergedFetchError =
        companiesError ?? usersError ?? uploadTemplatesError ?? sitesError;
    if (mergedFetchError) return <FetchErrorAlert error={mergedFetchError} />;
    if (
        areCompaniesLoading ||
        areUsersLoading ||
        areUploadTemplatesLoading ||
        (contract && areSitesLoading)
    )
        return <ContractFormSkeleton />;

    const onSubmit = async ({
        companyUuid,
        uploadTemplateId,
        applyProcessingLeadToAllSites: applyToAllSites,
        ...payload
    }) => {
        const body = {
            ...payload,
            startDate: formatDateISO(payload.startDate),
            endDate: formatDateISO(payload.endDate),
            reportDate: payload.reportDate && formatDateISO(payload.reportDate),
            uploadTemplateId: uploadTemplateId || null,
            dataAllowances: payload.dataAllowances.map((dataAllowance) => ({
                ...dataAllowance,
                monthlyAcquisitions: dataAllowance.monthlyAcquisitions.map(
                    ({ date, count }) => ({
                        year: getYear(date),
                        month: getMonth(date) + 1,
                        count: count ?? 0,
                    })
                ),
            })),
            processingComments:
                payload.processingComments &&
                JSON.stringify(payload.processingComments),
        };

        let updatedContract;
        try {
            updatedContract = await (contract
                ? mutate(
                      patch(
                          `${BACKEND_ROUTES.CONTRACT}/${contract.uuid}?dataAllowances=true&cloverfieldData=true&statistics=true`,
                          {
                              body,
                              forwardError: true,
                          }
                      ),
                      {
                          populateCache: (
                              updatedContract,
                              currentContract
                          ) => ({
                              ...currentContract,
                              ...updatedContract,
                          }),
                          revalidate: false,
                      }
                  )
                : post(`${BACKEND_ROUTES.COMPANY}/${companyUuid}/contracts`, {
                      body,
                      forwardError: true,
                  }));
        } catch (error) {
            openSnackbar(
                isImageSizeError(error)
                    ? IMAGE_SIZE_ERROR_MESSAGE
                    : error.message,
                "error"
            );
            return;
        }

        if (updatedContract) {
            if (applyToAllSites && contract) {
                try {
                    if (sites && sites.rows) {
                        await Promise.all(
                            sites.rows.map((site) =>
                                patch(`${BACKEND_ROUTES.SITE}/${site.uuid}`, {
                                    body: {
                                        processingLeadUuid:
                                            payload.processingLeadUuid,
                                    },
                                })
                            )
                        );
                        // Invalidate the sites cache to refresh the UI
                        globalMutate(
                            (key) =>
                                typeof key === "string" &&
                                key.includes(`${BACKEND_ROUTES.SITE}?`) &&
                                key.includes(`contractUuid=${contract.uuid}`)
                        );
                        openSnackbar(
                            `Campaign ${updatedContract.name} updated successfully. Processing lead applied to ${sites.rows.length} sites.`,
                            "success"
                        );
                    } else {
                        throw new Error("No sites found");
                    }
                } catch (error) {
                    openSnackbar(
                        `Campaign ${updatedContract.name} updated successfully, but failed to update some sites. Please refresh the page.`,
                        "warning"
                    );
                }
            } else {
                openSnackbar(
                    `Campaign ${updatedContract.name} ${
                        contract ? "updated" : "created"
                    } successfully.`,
                    "success"
                );
            }
        }

        if (!contract)
            navigate(
                `/${FRONTEND_ROUTES.ORDERS}/${FRONTEND_ROUTES.CONTRACT}/${updatedContract.uuid}`
            );
    };

    const onCancel = () => {
        contract
            ? reset()
            : navigate(
                  `/${FRONTEND_ROUTES.ORDERS}/${FRONTEND_ROUTES.CONTRACT}`
              );
    };

    // If users are nullish, the contract must have been given since basic role cannot
    // be creating a new contract
    const campaignManagers = users ?? [contract.CampaignManager];
    const processingLeads = users ?? [contract.ProcessingLead];
    const salespeople = users ?? [contract.Salesperson];

    const requiredLabel = !contract ? " *" : "";

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container alignItems="baseline" spacing={1}>
                    {isBasicRole && (
                        <Grid size={12}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ mt: 1 }}
                            >
                                Campaign Management
                            </Typography>
                        </Grid>
                    )}
                    <Grid container spacing={1} alignItems="center" size="grow">
                        <Grid
                            size={{
                                xs: 6,
                                xl: 4,
                            }}
                        >
                            <Controller
                                control={control}
                                name="companyUuid"
                                render={({ field: { ref, ...fieldProps } }) => (
                                    <TextField
                                        {...fieldProps}
                                        fullWidth
                                        id="companyUuid"
                                        label={`Company${requiredLabel}`}
                                        disabled={Boolean(contract)}
                                        select
                                        inputRef={ref}
                                        error={Boolean(errors.companyUuid)}
                                        helperText={errors.companyUuid?.message}
                                    >
                                        {companies ? (
                                            companies.rows.map((company) => (
                                                <MenuItem
                                                    key={company.uuid}
                                                    value={company.uuid}
                                                >
                                                    {company.name}
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem
                                                key={contract.companyUuid}
                                                value={contract.companyUuid}
                                            >
                                                {contract.Company?.name}
                                            </MenuItem>
                                        )}
                                    </TextField>
                                )}
                                rules={{
                                    required: INPUT_VALIDATION.REQUIRED,
                                }}
                            />
                        </Grid>

                        {isBasicRole && (
                            <Grid
                                display={{ xs: "block", xl: "none" }}
                                size={{
                                    xs: 4,
                                    xl: 0,
                                }}
                            />
                        )}
                        <Grid
                            size={{
                                xs: 6,
                                xl: 4,
                            }}
                        >
                            <TextField
                                name="name"
                                id="name"
                                label={`Name${requiredLabel}`}
                                fullWidth
                                type="text"
                                error={Boolean(errors.name)}
                                helperText={errors.name?.message}
                                {...register("name", {
                                    required: INPUT_VALIDATION.REQUIRED,
                                })}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                xl: 4,
                            }}
                        >
                            <Controller
                                control={control}
                                name="salespersonUuid"
                                render={({ field: { ref, ...fieldProps } }) => (
                                    <TextField
                                        {...fieldProps}
                                        fullWidth
                                        id="salespersonUuid"
                                        label={`Project Lead${requiredLabel}`}
                                        select
                                        inputRef={ref}
                                        error={Boolean(errors.salespersonUuid)}
                                        helperText={
                                            errors.salespersonUuid?.message
                                        }
                                    >
                                        {salespeople.map((user) => (
                                            <MenuItem
                                                key={user.uuid}
                                                value={user.uuid}
                                            >
                                                {user.firstName} {user.lastName}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                                rules={{
                                    required: INPUT_VALIDATION.REQUIRED,
                                }}
                            />
                        </Grid>

                        {!isBasicRole && (
                            <>
                                <Grid
                                    size={{
                                        xs: 6,
                                        xl: 4,
                                    }}
                                >
                                    <Controller
                                        control={control}
                                        name="campaignManagerUuid"
                                        render={({
                                            field: { ref, ...fieldProps },
                                        }) => (
                                            <TextField
                                                {...fieldProps}
                                                fullWidth
                                                id="campaignManagerUuid"
                                                label={`Campaign Manager${requiredLabel}`}
                                                select
                                                inputRef={ref}
                                                error={Boolean(
                                                    errors.campaignManagerUuid
                                                )}
                                                helperText={
                                                    errors.campaignManagerUuid
                                                        ?.message
                                                }
                                            >
                                                {campaignManagers.map(
                                                    (user) => (
                                                        <MenuItem
                                                            key={user.uuid}
                                                            value={user.uuid}
                                                        >
                                                            {user.firstName}{" "}
                                                            {user.lastName}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </TextField>
                                        )}
                                        rules={{
                                            required: INPUT_VALIDATION.REQUIRED,
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 6,
                                        xl: 4,
                                    }}
                                >
                                    <Controller
                                        control={control}
                                        name="processingLeadUuid"
                                        render={({
                                            field: { ref, ...fieldProps },
                                        }) => (
                                            <TextField
                                                {...fieldProps}
                                                fullWidth
                                                id="processingLeadUuid"
                                                label={`Processing Lead${requiredLabel}`}
                                                select
                                                inputRef={ref}
                                                error={Boolean(
                                                    errors.processingLeadUuid
                                                )}
                                                helperText={
                                                    errors.processingLeadUuid
                                                        ?.message
                                                }
                                            >
                                                {processingLeads.map((user) => (
                                                    <MenuItem
                                                        key={user.uuid}
                                                        value={user.uuid}
                                                    >
                                                        {user.firstName}{" "}
                                                        {user.lastName}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                        rules={{
                                            required: INPUT_VALIDATION.REQUIRED,
                                        }}
                                    />
                                </Grid>
                                {contract && sites.rows.length > 0 ? (
                                    <Grid
                                        size={{
                                            xs: 6,
                                            xl: 4,
                                        }}
                                    >
                                        <Controller
                                            control={control}
                                            name="applyProcessingLeadToAllSites"
                                            render={({
                                                field: {
                                                    ref,
                                                    value,
                                                    ...fieldProps
                                                },
                                            }) => (
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            {...fieldProps}
                                                            inputRef={ref}
                                                            checked={value}
                                                        />
                                                    }
                                                    label="Apply processing lead to all sites"
                                                />
                                            )}
                                        />
                                    </Grid>
                                ) : (
                                    <Grid
                                        size={{
                                            xs: 6,
                                            xl: 4,
                                        }}
                                    />
                                )}
                            </>
                        )}
                        <Grid
                            size={{
                                xs: 6,
                                xl: 4,
                            }}
                        >
                            <DatePickerField
                                dateLimit="start"
                                label={`Starting Date${requiredLabel}`}
                                isDisabled={isBasicRole}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                xl: 4,
                            }}
                        >
                            <DatePickerField
                                dateLimit="end"
                                label={`Ending Date${requiredLabel}`}
                                isDisabled={isBasicRole}
                            />
                        </Grid>

                        {!isBasicRole && (
                            <Grid
                                size={{
                                    xs: 6,
                                    xl: 4,
                                }}
                            >
                                <Controller
                                    name="reportDate"
                                    control={control}
                                    render={({
                                        field: { ref, ...fieldProps },
                                    }) => (
                                        <DatePicker
                                            {...fieldProps}
                                            slotProps={{
                                                field: {
                                                    clearable: true,
                                                },
                                                textField: {
                                                    fullWidth: true,
                                                    helperText: reportDateError,
                                                },
                                            }}
                                            format="yyyy-MM-dd"
                                            inputRef={ref}
                                            id="reportDate"
                                            label="Report Date"
                                            onError={(reason) => {
                                                switch (reason) {
                                                    case "invalidDate":
                                                        setDateError(
                                                            INPUT_VALIDATION.INVALID_DATE
                                                        );
                                                        break;

                                                    default:
                                                        setDateError(undefined);
                                                }
                                            }}
                                        />
                                    )}
                                    rules={{
                                        validate: () => !reportDateError,
                                    }}
                                />
                            </Grid>
                        )}
                        <Grid
                            size={{
                                xs: 6,
                                xl: 4,
                            }}
                        >
                            <Controller
                                control={control}
                                name="status"
                                render={({ field: { ref, ...fieldProps } }) => (
                                    <TextField
                                        {...fieldProps}
                                        fullWidth
                                        id="status"
                                        label={`Status${requiredLabel}`}
                                        select
                                        inputRef={ref}
                                        error={Boolean(errors.status)}
                                        helperText={errors.status?.message}
                                    >
                                        {Object.values(CONTRACT_STATUS).map(
                                            (status) => (
                                                <MenuItem
                                                    key={status}
                                                    value={status}
                                                >
                                                    {status}
                                                </MenuItem>
                                            )
                                        )}
                                    </TextField>
                                )}
                                rules={{
                                    required: INPUT_VALIDATION.REQUIRED,
                                }}
                            />
                        </Grid>
                        <Grid
                            display={{ xs: "block", xl: "none" }}
                            size={{
                                xs: 4,
                                xl: 0,
                            }}
                        />
                        <Grid size={12}>
                            <Controller
                                control={control}
                                name="goal"
                                render={({ field: { ref, ...fieldProps } }) => (
                                    <TextField
                                        {...fieldProps}
                                        id="goal"
                                        fullWidth
                                        label="Goal"
                                        type="text"
                                        error={Boolean(errors.goal)}
                                        helperText={errors.goal?.message}
                                        multiline
                                        placeholder="Describe the client objective here..."
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        rows={2}
                                    />
                                )}
                            />
                        </Grid>

                        {!isBasicRole && (
                            <>
                                <Grid size={12}>
                                    <Divider
                                        sx={{
                                            borderStyle: "dashed",
                                            borderWidth: "thin",
                                        }}
                                    />
                                    <Typography
                                        variant="h6"
                                        sx={{ color: "text.secondary" }}
                                    >
                                        Cloverfield fields
                                    </Typography>
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 6,
                                        xl: 4,
                                    }}
                                >
                                    <Controller
                                        control={control}
                                        name="system"
                                        render={({
                                            field: { ref, ...fieldProps },
                                        }) => (
                                            <TextField
                                                {...fieldProps}
                                                fullWidth
                                                id="system"
                                                label={`System${requiredLabel}`}
                                                select
                                                inputRef={ref}
                                                error={Boolean(errors.system)}
                                                helperText={
                                                    errors.system?.message
                                                }
                                            >
                                                {Object.values(
                                                    CF_CONTRACT_SYSTEMS
                                                ).map((system) => (
                                                    <MenuItem
                                                        key={system.value}
                                                        value={system.value}
                                                    >
                                                        {system.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                        rules={{
                                            required: INPUT_VALIDATION.REQUIRED,
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 6,
                                        xl: 4,
                                    }}
                                >
                                    <Controller
                                        control={control}
                                        name="uploadTemplateId"
                                        render={({
                                            field: { ref, ...fieldProps },
                                        }) => (
                                            <TextField
                                                {...fieldProps}
                                                fullWidth
                                                id="uploadTemplateId"
                                                label="Upload Template"
                                                select
                                                inputRef={ref}
                                                error={Boolean(
                                                    errors.uploadTemplateId
                                                )}
                                                helperText={
                                                    errors.uploadTemplateId
                                                        ?.message
                                                }
                                            >
                                                {uploadTemplates.rows
                                                    .concat({
                                                        id: "",
                                                        name: "-- Unset --",
                                                    })
                                                    .map((template) => (
                                                        <MenuItem
                                                            key={template.id}
                                                            value={template.id}
                                                        >
                                                            {template.name}
                                                        </MenuItem>
                                                    ))}
                                            </TextField>
                                        )}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 6,
                                        xl: 4,
                                    }}
                                >
                                    <Controller
                                        control={control}
                                        name="hasExperiment"
                                        render={({
                                            field: {
                                                ref,
                                                value,
                                                ...fieldProps
                                            },
                                        }) => (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        {...fieldProps}
                                                        inputRef={ref}
                                                        checked={value}
                                                    />
                                                }
                                                label="Enable plot map"
                                                labelPlacement="start"
                                                sx={{ ml: "auto" }}
                                            />
                                        )}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                    <Grid sx={{ alignSelf: "stretch" }}>
                        <Divider orientation="vertical" variant="middle" />
                    </Grid>
                    <Grid sx={{ display: "flex" }} size="grow">
                        <DataAllowanceForm />
                    </Grid>
                    {!contract && (
                        <Grid size={12}>
                            <Typography
                                variant="h5"
                                sx={{ color: "text.secondary" }}
                            >
                                Processing Comments
                            </Typography>
                            <Controller
                                name="processingComments"
                                control={control}
                                render={({ field }) => (
                                    <RichTextEditor
                                        defaultEditorState={field.value}
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                        </Grid>
                    )}
                    {(!contract || isDirty) && (
                        <Grid size={12}>
                            <Stack
                                direction="row"
                                spacing={3}
                                justifyContent="flex-end"
                            >
                                <Button type="button" onClick={onCancel}>
                                    Cancel
                                </Button>
                                <LoadingButton
                                    type="submit"
                                    variant="contained"
                                    loading={isSubmitting}
                                >
                                    {contract ? "Update" : "Create"}
                                </LoadingButton>
                            </Stack>
                        </Grid>
                    )}
                </Grid>
            </form>
        </FormProvider>
    );
};
